import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StarRating from '../StarRating/StarRating';
import Button from '../Button/Button';
import { AvisData, fmtDateFr, lotTechniqueLabel, lotGeoLabel, panierLabel } from './avisTypes';
import './AvisPrestataire.css';

interface AvisGroupesProps {
  avis: AvisData[];
  // 'prestataire' : groupe par entite (profil acheteur, "avis deposes").
  // 'acheteur'    : groupe par organisme (profil prestataire + annuaire, "avis recus").
  groupBy: 'prestataire' | 'acheteur';
  // Bouton "Ouvrir la commande" : la route operation est cote acheteur -> profil acheteur uniquement.
  showButton?: boolean;
  // Note GLOBALE (moyenne de tous les avis) affichee en entete ; null/absent -> pas d'entete.
  average?: number | null;
  loading?: boolean;
  emptyMessage?: string;
  // Vue publique (annuaire) : masque le numero d'operation (panier) et le filtre par operation.
  hideOperation?: boolean;
}

interface Group {
  id: number;
  name: string;
  logoUrl: string | null;
  average: number;
  items: AvisData[];
}

// Logo du groupe (prestataire ou acheteur) avec repli sur les initiales en cas d'erreur.
const GroupLogo: React.FC<{ url: string | null; name: string }> = ({ url, name }) => {
  const [hasError, setHasError] = useState(false);
  const initials = name.trim().slice(0, 2).toUpperCase() || '?';
  return (
    <span className="avis-groupe-logo" aria-hidden="true">
      {url && !hasError
        ? <img src={url} alt="" onError={() => setHasError(true)} />
        : <span className="avis-groupe-logo-txt">{initials}</span>}
    </span>
  );
};

/**
 * Avis groupes en accordeon : une ligne par contrepartie (prestataire OU acheteur selon `groupBy`)
 * avec note moyenne + nombre, depliable pour le detail par commande. Vue compacte -> peu de scroll.
 * Filtres : mots-cles (commentaire) + menus deroulants (contrepartie, lot technique, lot geo,
 * operation, note).
 */
const AvisGroupes: React.FC<AvisGroupesProps> = ({ avis, groupBy, showButton, average, loading, emptyMessage, hideOperation }) => {
  const navigate = useNavigate();
  const byPrestataire = groupBy === 'prestataire';

  // Filtres
  const [keyword, setKeyword] = useState('');
  const [counterpartyId, setCounterpartyId] = useState(0); // 0 = tous
  const [lotTech, setLotTech] = useState('');
  const [lotGeo, setLotGeo] = useState('');
  const [operationId, setOperationId] = useState(0);
  const [noteFilter, setNoteFilter] = useState(0);

  const isFiltering = keyword.trim() !== '' || counterpartyId > 0 || lotTech !== '' || lotGeo !== '' || operationId > 0 || noteFilter > 0;

  // Options des menus, derivees des avis presents.
  const counterparties = useMemo(() => {
    const m = new Map<number, string>();
    avis.forEach((a) => {
      const id = byPrestataire ? a.entite_id : a.organisme_id;
      if (!m.has(id)) m.set(id, (byPrestataire ? a.entite_nom : a.organisme_nom) ?? `#${id}`);
    });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [avis, byPrestataire]);

  const lotsTech = useMemo(() => {
    const m = new Map<string, string>();
    avis.forEach((a) => { if (a.lot_technique_code) m.set(a.lot_technique_code, lotTechniqueLabel(a)); });
    return Array.from(m, ([code, label]) => ({ code, label })).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  }, [avis]);

  const lotsGeo = useMemo(() => {
    const m = new Map<string, string>();
    avis.forEach((a) => { if (a.departement_code) m.set(a.departement_code, lotGeoLabel(a)); });
    return Array.from(m, ([code, label]) => ({ code, label })).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  }, [avis]);

  // Operation : le vrai numero (id - numero de commande) uniquement ici.
  const operations = useMemo(() => {
    const m = new Map<number, string>();
    avis.forEach((a) => { if (a.operation_id) m.set(a.operation_id, `${a.operation_id} - ${a.numero_commande ?? '—'}`); });
    return Array.from(m, ([id, label]) => ({ id, label })).sort((a, b) => a.id - b.id);
  }, [avis]);

  const filteredAvis = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return avis.filter((a) => {
      if (counterpartyId > 0 && (byPrestataire ? a.entite_id : a.organisme_id) !== counterpartyId) return false;
      if (lotTech && a.lot_technique_code !== lotTech) return false;
      if (lotGeo && a.departement_code !== lotGeo) return false;
      if (operationId > 0 && a.operation_id !== operationId) return false;
      if (noteFilter > 0 && a.note !== noteFilter) return false;
      // Le champ texte ne cherche QUE dans le commentaire de l'avis (mots-cles).
      if (term && !(a.commentaire ?? '').toLowerCase().includes(term)) return false;
      return true;
    });
  }, [avis, keyword, counterpartyId, lotTech, lotGeo, operationId, noteFilter, byPrestataire]);

  const groups = useMemo<Group[]>(() => {
    const idOf = (a: AvisData) => (byPrestataire ? a.entite_id : a.organisme_id);
    const nameOf = (a: AvisData) => (byPrestataire ? a.entite_nom : a.organisme_nom) ?? (byPrestataire ? 'Prestataire' : 'Acheteur');
    const logoOf = (a: AvisData) => (byPrestataire ? a.entite_logo_url : a.organisme_logo_url);

    const map = new Map<number, AvisData[]>();
    filteredAvis.forEach((a) => {
      const items = map.get(idOf(a)) ?? [];
      items.push(a);
      map.set(idOf(a), items);
    });
    return Array.from(map.entries())
      .map(([id, items]) => ({
        id,
        name: nameOf(items[0]),
        logoUrl: logoOf(items[0]),
        average: Math.round((items.reduce((s, a) => s + a.note, 0) / items.length) * 10) / 10,
        items: items.slice().sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '')),
      }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [filteredAvis, byPrestataire]);

  // Accordeons : mode normal -> un seul ouvert (openId). En recherche -> tous ouverts par defaut
  // MAIS retractables individuellement (collapsedSearch). On reinitialise a la sortie de recherche.
  const [openId, setOpenId] = useState<number | null>(null);
  const [collapsedSearch, setCollapsedSearch] = useState<Set<number>>(new Set());
  const prevFiltering = useRef(false);
  useEffect(() => {
    if (prevFiltering.current && !isFiltering) setCollapsedSearch(new Set());
    prevFiltering.current = isFiltering;
  }, [isFiltering]);

  const isGroupOpen = (id: number) => (isFiltering ? !collapsedSearch.has(id) : openId === id);
  const toggleGroup = (id: number) => {
    if (isFiltering) {
      setCollapsedSearch((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    } else {
      setOpenId(openId === id ? null : id);
    }
  };

  if (loading) {
    return (
      <div className="avis-groupes">
        {[0, 1, 2].map((i) => <div key={i} className="avis-groupe-head avis-groupe-head--skeleton" aria-hidden="true" />)}
      </div>
    );
  }

  if (!avis.length) {
    return <p className="avis-list-empty">{emptyMessage ?? 'Aucun avis pour le moment.'}</p>;
  }

  return (
    <div className="avis-groupes">
      <div className="avis-search-bar">
        <input
          type="text"
          className="avis-search-input"
          placeholder="Mots-clés dans l'avis…"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          aria-label="Rechercher des mots-clés dans les avis"
        />
        <select className="avis-filter-select" value={counterpartyId} onChange={(e) => setCounterpartyId(Number(e.target.value))} aria-label="Filtrer par contrepartie">
          <option value={0}>{byPrestataire ? 'Tous les prestataires' : 'Tous les acheteurs'}</option>
          {counterparties.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="avis-filter-select" value={lotTech} onChange={(e) => setLotTech(e.target.value)} aria-label="Filtrer par lot technique">
          <option value="">Tous les lots techniques</option>
          {lotsTech.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        <select className="avis-filter-select" value={lotGeo} onChange={(e) => setLotGeo(e.target.value)} aria-label="Filtrer par lot géographique">
          <option value="">Tous les lots géographiques</option>
          {lotsGeo.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        {!hideOperation && (
          <select className="avis-filter-select" value={operationId} onChange={(e) => setOperationId(Number(e.target.value))} aria-label="Filtrer par opération">
            <option value={0}>Toutes les opérations</option>
            {operations.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        )}
        <select className="avis-filter-select" value={noteFilter} onChange={(e) => setNoteFilter(Number(e.target.value))} aria-label="Filtrer par note">
          <option value={0}>Toutes les notes</option>
          <option value={5}>5 étoiles</option>
          <option value={4}>4 étoiles</option>
          <option value={3}>3 étoiles</option>
          <option value={2}>2 étoiles</option>
          <option value={1}>1 étoile</option>
        </select>
        {isFiltering && (
          <span className="avis-search-count">{filteredAvis.length} résultat{filteredAvis.length > 1 ? 's' : ''}</span>
        )}
      </div>
      {average != null && (
        <div className="avis-groupes-entete">
          <StarRating value={average} readOnly />
          <span className="avis-groupes-moyenne">{average}/5</span>
          <span className="avis-groupes-total">{avis.length} avis</span>
        </div>
      )}
      {groups.length === 0 ? (
        <p className="avis-list-empty">Aucun avis ne correspond à votre recherche.</p>
      ) : groups.map((g) => {
        const isOpen = isGroupOpen(g.id);
        return (
          <div key={g.id} className={`avis-groupe${isOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              className="avis-groupe-head"
              onClick={() => toggleGroup(g.id)}
              aria-expanded={isOpen}
            >
              <GroupLogo url={g.logoUrl} name={g.name} />
              <span className="avis-groupe-nom">{g.name}</span>
              <span className="avis-groupe-count">{g.items.length} avis</span>
              <span className="avis-groupe-note">
                <StarRating value={g.average} readOnly />
                <span className="avis-groupe-note-val">{g.average}/5</span>
              </span>
              <svg className="avis-groupe-chevron" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isOpen && (
              <div className="avis-groupe-body">
                {g.items.map((a) => (
                  <div key={a.id} className="avis-ligne">
                    <div className="avis-ligne-main">
                      <div className="avis-ligne-ids">
                        <span className="avis-ligne-id"><span className="avis-ligne-id-k">Lot technique</span>{lotTechniqueLabel(a)}</span>
                        <span className="avis-ligne-id"><span className="avis-ligne-id-k">Lot géographique</span>{lotGeoLabel(a)}</span>
                        {!hideOperation && (
                          <span className="avis-ligne-id"><span className="avis-ligne-id-k">Panier</span>{panierLabel(a)}</span>
                        )}
                      </div>
                      {a.updated_at && (
                        <div className="avis-ligne-meta"><span>Avis du {fmtDateFr(a.updated_at)}</span></div>
                      )}
                      {a.commentaire && <p className="avis-ligne-comment">« {a.commentaire} »</p>}
                      {showButton && a.operation_id && (
                        <div className="avis-ligne-action">
                          <Button
                            value="Ouvrir la commande"
                            variant="secondary"
                            size="small"
                            onClick={() => navigate(`/operation-${a.type_commande.toLowerCase()}/${a.operation_id}`)}
                          />
                        </div>
                      )}
                    </div>
                    <span className="avis-ligne-note">
                      <StarRating value={a.note} readOnly />
                      <span className="avis-ligne-note-val">{a.note}/5</span>
                    </span>
                    <span className="avis-ligne-chevron-space" aria-hidden="true" />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AvisGroupes;
